import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '@/lib/api-client';
import type { components } from '@/lib/api-types-administration';

export type RoleAssignment = components['schemas']['RoleAssignment'];

export type RoleAssignmentsState =
  | { readonly status: 'loading'; readonly items: readonly RoleAssignment[] }
  | {
      readonly status: 'ready';
      readonly items: readonly RoleAssignment[];
      /** False when the place holds more grants than the client reads at once: counts are then partial. */
      readonly complete: boolean;
    }
  | { readonly status: 'error'; readonly code: unknown; readonly httpStatus: number | undefined };

/** A place holds dozens of grants, not thousands; past this many pages the counts say so. */
const MAX_PAGES = 10;

function readPage(placeKind: 'tenants' | 'organizations', placeId: string, cursor: string | null) {
  return apiClient.GET('/v1/administration/{placeKind}/{placeId}/role-assignments', {
    params: {
      path: { placeKind, placeId },
      query: { sort: 'person', ...(cursor !== null ? { cursor } : {}) },
    },
  });
}

/**
 * Every grant at a place (GET …/role-assignments), read page by page, for Ruoli e accessi: the same
 * facts shown by role and by person (FR-AD-5), so one read feeds both views and their counts
 * agree with each other.
 */
export function useRoleAssignments(
  placeKind: 'tenants' | 'organizations',
  placeId: string,
): { readonly state: RoleAssignmentsState; readonly reload: () => void } {
  const [state, setState] = useState<RoleAssignmentsState>({ status: 'loading', items: [] });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState((current) => ({
      status: 'loading',
      items: current.status === 'error' ? [] : current.items,
    }));
    void (async () => {
      const items: RoleAssignment[] = [];
      let cursor: string | null = null;
      for (let page = 0; page < MAX_PAGES; page += 1) {
        const { data, error, response } = await readPage(placeKind, placeId, cursor);
        if (cancelled) {
          return;
        }
        if (data === undefined || !response.ok) {
          setState({ status: 'error', code: error, httpStatus: response.status });
          return;
        }
        items.push(...data.items);
        cursor = data.nextCursor;
        if (cursor === null) {
          break;
        }
      }
      setState({ status: 'ready', items, complete: cursor === null });
    })().catch(() => {
      if (!cancelled) {
        setState({ status: 'error', code: null, httpStatus: undefined });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [placeKind, placeId, attempt]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);
  return { state, reload };
}
